import React, { useState, useEffect } from "react";
import {
  getAuth,
  onAuthStateChanged,
  sendPasswordResetEmail,
  multiFactor,
  PhoneAuthProvider,
  RecaptchaVerifier,
  updateProfile,
} from "firebase/auth";
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  deleteDoc,
} from "firebase/firestore";
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import {
  Avatar,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  TextField,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import { db } from "../firebase-config";
import Sidebar from "../components/ui/Sidebar";
import PageHeader from "../components/ui/PageHeader";
import TextInputComponent from "../components/ui/TextInputComponent";

const UserProfile = () => {
  const [userData, setUserData] = useState({ name: "", email: "", altEmail: "", phone: "", role: "", photoURL: "" });
  const [activeSessions, setActiveSessions] = useState([]);
  const [mfaEnrolled, setMfaEnrolled] = useState(false);
  const [mfaPhone, setMfaPhone] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationId, setVerificationId] = useState(null);
  const [recaptcha, setRecaptcha] = useState(null);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const authInstance = getAuth();
  const user = authInstance.currentUser;

  // ✅ This useEffect now runs only once to set up the Recaptcha verifier
  useEffect(() => {
    // Prevent re-initialization
    if (recaptcha) return;

    try {
      const verifier = new RecaptchaVerifier(
        authInstance, 
        "recaptcha-container", 
        { size: "invisible" }
      );
      setRecaptcha(verifier);
    } catch (error) {
      console.error("Error initializing Recaptcha:", error);
    }
  }, [authInstance, recaptcha]);


  // ✅ This useEffect is now only responsible for fetching data when the user changes
  useEffect(() => {
    const fetchData = async (currentUser) => {
      if (!currentUser) return;
      const userRef = doc(db, "users", currentUser.uid);
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        setUserData({
          name: userDoc.data().name || "",
          email: currentUser.email,
          altEmail: userDoc.data().altEmail || "",
          phone: userDoc.data().phone || "",
          role: userDoc.data().role || "User",
          photoURL: userDoc.data().photoURL || "",
        });
      }

      const sessionsQuery = query(collection(db, "activeSessions"), where("email", "==", currentUser.email));
      const sessionsSnapshot = await getDocs(sessionsQuery);
      const sessions = sessionsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setActiveSessions(sessions);

      const idTokenResult = await currentUser.getIdTokenResult(true);
      const mfaClaims = idTokenResult.claims.multiFactor || {};
      if (mfaClaims.enrolledFactors && mfaClaims.enrolledFactors.length > 0) {
        const enrolledFactor = mfaClaims.enrolledFactors.find(factor => factor.factorId === "phone");
        if (enrolledFactor) {
          setMfaEnrolled(true);
          setMfaPhone(enrolledFactor.phoneNumber || "Unknown");
        }
      }
    };

    const unsubscribeAuth = onAuthStateChanged(authInstance, (user) => {
      if (user) {
        fetchData(user);
      } else {
        setUserData({ name: "", email: "", altEmail: "", phone: "", role: "", photoURL: "" });
        setActiveSessions([]);
        setMfaEnrolled(false);
        setMfaPhone("");
      }
    });

    return () => unsubscribeAuth();
  }, [authInstance]); // This effect depends only on the authInstance

  const handleUpdateProfile = async () => {
    if (!user) return;
    const userRef = doc(db, "users", user.uid);
    await updateDoc(userRef, {
      name: userData.name,
      altEmail: userData.altEmail,
      phone: userData.phone,
      photoURL: userData.photoURL,
    });
    await updateProfile(authInstance.currentUser, { displayName: userData.name });
    alert("Profile updated!");
  };

  const handleSendPasswordReset = async () => {
    await sendPasswordResetEmail(authInstance, user.email);
    alert("Password reset email sent.");
  };

  const handleProfileImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const storage = getStorage();
    const storageRef = ref(storage, `profilePictures/${user.uid}`);
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);
    await updateDoc(doc(db, "users", user.uid), { photoURL: downloadURL });
    await updateProfile(authInstance.currentUser, { photoURL: downloadURL });
    setUserData(prev => ({ ...prev, photoURL: downloadURL }));
    setUploading(false);
  };

  const handleEnableMfa = async () => {
    if (!authInstance.currentUser || !mfaPhone) return;
    setMfaLoading(true);
    try {
      const provider = new PhoneAuthProvider(authInstance);
      const verificationId = await provider.verifyPhoneNumber(mfaPhone, recaptcha);
      setVerificationId(verificationId);
      alert("Verification code sent.");
    } catch (error) {
      alert("Failed to send verification code.");
    } finally {
      setMfaLoading(false);
    }
  };

  const handleVerifyMfa = async () => {
    if (!verificationId || !verificationCode) return;
    setMfaLoading(true);
    try {
      const credential = PhoneAuthProvider.credential(verificationId, verificationCode);
      await multiFactor(authInstance.currentUser).enroll(credential, mfaPhone);
      setMfaEnrolled(true);
      alert("MFA enabled!");
    } catch (error) {
      alert("Failed to verify MFA.");
    } finally {
      setMfaLoading(false);
    }
  };

  const handleDisableMfa = async () => {
    if (!authInstance.currentUser) return;
    setMfaLoading(true);
    try {
      await authInstance.currentUser.reload();
      const enrolledFactors = multiFactor(authInstance.currentUser).enrolledFactors;
      if (enrolledFactors.length === 0) {
        alert("No MFA factors found.");
        return;
      }
      await multiFactor(authInstance.currentUser).unenroll(enrolledFactors[0].uid);
      setMfaEnrolled(false);
      setMfaPhone("");
      alert("MFA has been disabled.");
    } catch (error) {
      alert("Failed to disable MFA.");
    } finally {
      setMfaLoading(false);
    }
  };

  const handleUpdateMfaPhone = () => setMfaEnrolled(false);


const handleDeleteSession = async (session) => {
try {
const user = authInstance.currentUser;
if (!user) return;

const idToken = await user.getIdToken(true);

await fetch("https://us-central1-integrity-resolutions-inc.cloudfunctions.net/revokeUserSession", {
  method: "POST",
  headers: {
    "Authorization": `Bearer ${idToken}`,
    "Content-Type": "application/json",
  },
  body: JSON.stringify({ 
    userIdToRevoke: user.uid, 
    sessionIdToDelete: session.id 
  }),
});

// Optimistically remove the session from the UI
setActiveSessions(prev => prev.filter(s => s.id !== session.id));

} catch (error) {
  console.error("Error deleting session:", error);
}
  };

  const handleInputChange = (field, value) => {
    setUserData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="manage-users-container">
      <Sidebar />
      <div className="manage-users-content">
        <PageHeader title="User Profile" showSearch={false} />

        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title="User Information" />
              <CardContent>
                <Grid container direction="column" spacing={2} alignItems="center">
                  <Grid item>
                    <Avatar src={userData.photoURL} sx={{ width: 100, height: 100 }} />
                  </Grid>
                  <Grid item>
                    <input
                      accept="image/*"
                      id="upload-photo"
                      type="file"
                      hidden
                      onChange={handleProfileImageUpload}
                    />
                    <label htmlFor="upload-photo">
                      <Button component="span" variant="outlined" disabled={uploading}>
                        {uploading ? "Uploading..." : "Upload Photo"}
                      </Button>
                    </label>
                  </Grid>
                  <Grid item sx={{ width: "100%" }}>
                    <TextInputComponent
                      label="Display Name"
                      value={userData.name}
                      onChange={(e) => handleInputChange("name", e.target.value)}
                      required
                    />
                    <TextInputComponent
                      label="Email"
                      value={userData.email}
                      readOnly
                    />
                    <TextInputComponent
                      label="Alternate Email"
                      value={userData.altEmail}
                      onChange={(e) => handleInputChange("altEmail", e.target.value)}
                    />
                    <TextInputComponent
                      label="Role"
                      value={userData.role}
                      readOnly
                    />
                    <TextInputComponent
                      label="Phone Number"
                      value={userData.phone}
                      onChange={(e) => handleInputChange("phone", e.target.value)}
                    />
                  </Grid>
                  <Grid item>
                    <Button variant="contained" onClick={handleUpdateProfile} sx={{ mr: 1 }}>Save</Button>
                    <Button variant="outlined" onClick={handleSendPasswordReset}>Reset Password</Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* MFA */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title="Multi-Factor Authentication" />
              <CardContent>
                {mfaEnrolled ? (
                  <>
                    <Typography gutterBottom>MFA enabled for: {mfaPhone}</Typography>
                    <Button fullWidth variant="contained" onClick={() => setMfaEnrolled(false)} disabled={mfaLoading}>Update MFA Phone</Button>
                    <Button fullWidth variant="outlined" color="error" onClick={handleDisableMfa} disabled={mfaLoading}>Disable MFA</Button>
                  </>
                ) : (
                  <>
                    <TextField fullWidth label="Phone Number" value={mfaPhone} onChange={(e) => setMfaPhone(e.target.value)} margin="dense" />
                    <Button variant="contained" fullWidth onClick={handleEnableMfa} disabled={mfaLoading}>Send Code</Button>
                    <TextField fullWidth label="Verification Code" value={verificationCode} onChange={(e) => setVerificationCode(e.target.value)} margin="dense" />
                    <Button fullWidth variant="contained" color="success" onClick={handleVerifyMfa} disabled={mfaLoading}>Verify</Button>
                    <div id="recaptcha-container" />
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Active Sessions */}
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Active Sessions" />
              <CardContent>
                {activeSessions.length === 0 ? (
                  <Typography>No active sessions.</Typography>
                ) : (
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Login Time</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {activeSessions.map((s) => (
                        <TableRow key={s.id}>
                          <TableCell>{s.loginTime ? new Date(s.loginTime.seconds * 1000).toLocaleString() : "N/A"}</TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              onClick={() => handleDeleteSession(s.id)}
                            >
                              End Session
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Activity & Permissions */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title="Recent Activity" />
              <CardContent>
                <Typography>Activity logs and usage history will be available here soon.</Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title="Permissions & Access" />
              <CardContent>
                <Typography>
                  {userData.role === "admin"
                    ? "Full access to admin dashboard and user management."
                    : "Standard user access. Contact support for role upgrades."}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </div>
    </div>
  );
};

export default UserProfile;